import { Suspense, lazy } from 'react'
import { ArrowLeft, Download, Loader2 } from 'lucide-react'
import { useNavigate, useParams } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  fetchAllCharts,
  fetchAllResults,
  fetchAllSpreadsheets,
  getResultCsvUrl,
  renameChart,
  renameResult,
  type ApiConvChart,
  type ApiConvResult,
  type ApiConvSpreadsheet,
} from '../../lib/chatApi'
import { EditableTitle } from './EditableTitle'
import type { ChartPayload } from './ChartEmbed'
import type { SpreadsheetPayload } from './SpreadsheetEmbed'
import { useActiveWorkspace } from '../../lib/workspace'

const ResultGrid = lazy(() => import('../../components/ui/ResultGrid'))
const ChartEmbed = lazy(() => import('./ChartEmbed'))
const SpreadsheetEmbed = lazy(() => import('./SpreadsheetEmbed'))

export function ArtifactDetailPage() {
  const { kind, id } = useParams({ from: '/app/artifacts/$kind/$id' })
  const navigate = useNavigate()
  const goBack = () => navigate({ to: '/assistant' })

  if (kind !== 'chart' && kind !== 'result' && kind !== 'spreadsheet') {
    return <NotFound onBack={goBack} />
  }

  return (
    <div className="flex flex-col flex-1 h-full bg-app-bg min-w-0">
      <div className="flex items-center gap-3 h-14 px-4 md:px-6 shrink-0 border-b border-border-default/50">
        <button
          onClick={goBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft size={16} />
          Artifacts
        </button>
      </div>

      {kind === 'chart' && <ChartDetail id={id} />}
      {kind === 'result' && <ResultDetail id={id} />}
      {kind === 'spreadsheet' && <SpreadsheetDetail id={id} />}
    </div>
  )
}

function NotFound({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex flex-col flex-1 h-full bg-app-bg min-w-0">
      <div className="flex items-center gap-3 h-14 px-4 md:px-6 shrink-0 border-b border-border-default/50">
        <button
          onClick={onBack}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors"
        >
          <ArrowLeft size={16} />
          Artifacts
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500">We couldn't find that artifact.</p>
      </div>
    </div>
  )
}

function LoadingBody() {
  return (
    <div className="flex-1 flex items-center justify-center text-sm text-gray-500">
      <Loader2 size={16} className="animate-spin mr-2" />
      Loading…
    </div>
  )
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString(undefined, {
      year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
    })
  } catch {
    return iso
  }
}

// --- Result detail --------------------------------------------------------

function ResultDetail({ id }: { id: string }) {
  const queryClient = useQueryClient()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data: results, isLoading } = useQuery({
    queryKey: ['allResults', activeWorkspaceKey],
    queryFn: () => fetchAllResults(activeWorkspaceKey),
    enabled: !!activeWorkspaceKey,
  })
  const result = results?.find((r) => r.id === id)

  if (isLoading && !results) return <LoadingBody />
  if (!result) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500">We couldn't find that artifact.</p>
      </div>
    )
  }

  const patchCache = (title: string) => {
    queryClient.setQueryData<ApiConvResult[] | undefined>(['allResults', activeWorkspaceKey], (old) =>
      old?.map((r) => (r.id === result.id ? { ...r, title } : r)),
    )
  }

  const columns = (result.columns as { name: string; dtype: string }[]) ?? []
  const rows = (result.rows as unknown[][]) ?? []

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-12 py-6 md:py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <EditableTitle
              value={result.title}
              canEdit={true}
              trigger="doubleClick"
              className="block text-lg font-semibold text-gray-900 truncate"
              inputClassName="w-full bg-white border border-gray-300 rounded px-1.5 py-0.5 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              ariaLabel="Rename result"
              onSave={async (next) => {
                const prev = result.title
                patchCache(next)
                try {
                  await renameResult(activeWorkspaceKey, result.id, next)
                  queryClient.invalidateQueries({ queryKey: ['conversationResults'] })
                } catch (e) {
                  patchCache(prev)
                  throw e
                }
              }}
            />
          </div>
          <div className="shrink-0 flex items-center gap-1.5">
            <DownloadCsvLink workspaceKey={activeWorkspaceKey} resultId={result.id} title={result.title} />
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-xs text-gray-500 mb-0.5">Rows</div>
            <div className="text-lg font-semibold text-gray-900">{rows.length.toLocaleString()}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-xs text-gray-500 mb-0.5">Columns</div>
            <div className="text-lg font-semibold text-gray-900">{columns.length}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 col-span-2 sm:col-span-1">
            <div className="text-xs text-gray-500 mb-0.5">Created</div>
            <div className="text-sm font-medium text-gray-900">{formatDate(result.created_at)}</div>
          </div>
        </div>

        <div>
          <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Data</h2>
          {rows.length === 0 ? (
            <p className="text-sm text-gray-500">No rows to display.</p>
          ) : (
            <Suspense
              fallback={
                <div className="flex items-center justify-center py-10 bg-gray-50 rounded text-sm text-gray-500">
                  <Loader2 size={16} className="animate-spin mr-2" />
                  Loading preview...
                </div>
              }
            >
              <ResultGrid result={result} />
            </Suspense>
          )}
        </div>

        {columns.length > 0 && (
          <div>
            <h2 className="text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">Columns</h2>
            <div className="bg-white rounded-lg border border-gray-200 divide-y divide-gray-100">
              {columns.map((col) => (
                <div key={col.name} className="flex justify-between px-4 py-2.5 text-sm">
                  <span className="font-medium text-gray-700">{col.name}</span>
                  <span className="text-gray-400 font-mono text-xs">{col.dtype}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function DownloadCsvLink({ workspaceKey, resultId, title }: { workspaceKey: string; resultId: string; title: string }) {
  return (
    <a
      href={getResultCsvUrl(workspaceKey, resultId)}
      download={`${title || 'result'}.csv`}
      data-testid="artifact-detail-csv-download"
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors"
    >
      <Download size={13} />
      CSV
    </a>
  )
}

// --- Chart detail ---------------------------------------------------------

function ChartDetail({ id }: { id: string }) {
  const queryClient = useQueryClient()
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data: charts, isLoading } = useQuery({
    queryKey: ['allCharts', activeWorkspaceKey],
    queryFn: () => fetchAllCharts(activeWorkspaceKey),
    enabled: !!activeWorkspaceKey,
  })
  const chart = charts?.find((c) => c.id === id)

  if (isLoading && !charts) return <LoadingBody />
  if (!chart) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500">We couldn't find that artifact.</p>
      </div>
    )
  }

  const patchCache = (title: string) => {
    queryClient.setQueryData<ApiConvChart[] | undefined>(['allCharts', activeWorkspaceKey], (old) =>
      old?.map((c) => (c.artifact_id === chart.artifact_id ? { ...c, title } : c)),
    )
  }

  const payload: ChartPayload = {
    id: chart.id,
    title: chart.title,
    spec: chart.spec as Record<string, unknown>,
    data: chart.data as { columns: { name: string; dtype: string }[]; rows: Record<string, unknown>[] },
    artifactId: chart.artifact_id,
    versionNum: chart.version_num,
    createdAt: chart.created_at,
    savedByUser: chart.saved_by_user,
    messageId: chart.message_id ?? null,
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-12 py-6 md:py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <EditableTitle
              value={chart.title}
              canEdit={true}
              trigger="doubleClick"
              className="block text-lg font-semibold text-gray-900 truncate"
              inputClassName="w-full bg-white border border-gray-300 rounded px-1.5 py-0.5 text-lg font-semibold text-gray-900 focus:outline-none focus:ring-2 focus:ring-slate-400"
              ariaLabel="Rename chart"
              onSave={async (next) => {
                const prev = chart.title
                patchCache(next)
                try {
                  await renameChart(activeWorkspaceKey, chart.artifact_id, next)
                  queryClient.invalidateQueries({ queryKey: ['conversationCharts'] })
                } catch (e) {
                  patchCache(prev)
                  throw e
                }
              }}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-xs text-gray-500 mb-0.5">Created</div>
            <div className="text-sm font-medium text-gray-900">{formatDate(chart.created_at)}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-xs text-gray-500 mb-0.5">Version</div>
            <div className="text-sm font-medium text-gray-900">v{chart.version_num}</div>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-6 py-5">
          <Suspense fallback={<div className="text-gray-400 py-16 text-center">Loading chart…</div>}>
            <ChartEmbed chart={payload} />
          </Suspense>
        </div>
      </div>
    </div>
  )
}

// --- Spreadsheet detail ---------------------------------------------------

function SpreadsheetDetail({ id }: { id: string }) {
  const { activeWorkspaceKey } = useActiveWorkspace()
  const { data: spreadsheets, isLoading } = useQuery({
    queryKey: ['allSpreadsheets', activeWorkspaceKey],
    queryFn: () => fetchAllSpreadsheets(activeWorkspaceKey),
    enabled: !!activeWorkspaceKey,
  })

  // Route params land here as the artifact_id (stable across versions);
  // pick the latest version of that artifact to render, fall back to a
  // direct id match for legacy links.
  const latestForArtifact = spreadsheets
    ?.filter((s) => s.artifact_id === id)
    .sort((a, b) => b.version_num - a.version_num)[0]
  const byRowId = spreadsheets?.find((s) => s.id === id)
  const spreadsheet: ApiConvSpreadsheet | undefined = latestForArtifact ?? byRowId

  if (isLoading && !spreadsheets) return <LoadingBody />
  if (!spreadsheet) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <p className="text-sm text-gray-500">We couldn't find that artifact.</p>
      </div>
    )
  }

  const allVersions: SpreadsheetPayload[] = (spreadsheets ?? [])
    .filter((s) => s.artifact_id === spreadsheet.artifact_id)
    .sort((a, b) => a.version_num - b.version_num)
    .map((s) => ({
      id: s.id,
      title: s.title,
      artifactId: s.artifact_id,
      versionNum: s.version_num,
      createdAt: s.created_at,
      savedByUser: s.saved_by_user,
      messageId: s.message_id ?? null,
    }))
  const currentPayload = allVersions[allVersions.length - 1]

  return (
    <div className="flex-1 overflow-y-auto px-4 sm:px-6 lg:px-12 py-6 md:py-8">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex-1 min-w-0 flex items-center gap-2">
            <span className="block text-lg font-semibold text-gray-900 truncate">
              {spreadsheet.title}
            </span>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-xs text-gray-500 mb-0.5">Size</div>
            <div className="text-lg font-semibold text-gray-900">
              {formatBytes(spreadsheet.byte_size)}
            </div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <div className="text-xs text-gray-500 mb-0.5">Version</div>
            <div className="text-lg font-semibold text-gray-900">v{spreadsheet.version_num}</div>
          </div>
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3 col-span-2 sm:col-span-1">
            <div className="text-xs text-gray-500 mb-0.5">Created</div>
            <div className="text-sm font-medium text-gray-900">{formatDate(spreadsheet.created_at)}</div>
          </div>
        </div>

        <Suspense fallback={<LoadingBody />}>
          <SpreadsheetEmbed spreadsheet={currentPayload} versions={allVersions} />
        </Suspense>
      </div>
    </div>
  )
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}
